import { Component, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Salutation } from '@lsq-frontend/remote-services/enterprise/common';
import { Color, Masks, Modal, Regex, Unmasks } from '@lsq-frontend/common-ui';
import { Icons } from '@lsq-frontend/common-ui';
import {
    EmailI18nValidator,
    PhoneI18nValidator,
    StringI18nValidator
} from '@lsq-frontend/common-ui';
import { ModalService, ModalWorkflowService } from '@lsq-frontend/common-ui';
import { I18n } from '@ngx-translate/i18n-polyfill';
import { ManageUsersService } from '../../services/manage-users.service';

@Component({
    selector: 'add-user-personal-details-modal',
    template: `
        <form class="modal-content" [formGroup]="formGroup" (ngSubmit)="continueWorkflow()">
            <div class="modal-header">
                <h1>{{ modal?.title }}</h1>
                <lsq-button
                    *ngIf="modal?.showCloseButton"
                    appearance="transparent"
                    class="close"
                    ariaLabel="Close"
                    (buttonClick)="modalService.closeModal()"
                >
                    <lsq-icon aria-hidden="true" [type]="icons.Close" [color]="color.Black">
                    </lsq-icon>
                </lsq-button>
            </div>
            <div class="modal-body">
                <p>{{ subtitle }}</p>

                <fieldset class="personal-details-fieldset">
                    <legend
                        class="h3"
                        [style.margin-bottom]="'1rem'"
                        i18n="Title for the 'Add User' form"
                    >
                        Personal Details
                    </legend>
                    <div class="row">
                        <div class="col-sm-6">
                            <lsq-input
                                class="e2e-first-name"
                                formControlName="firstName"
                                i18n-label="New user first name input"
                                label="First Name"
                                [name]="'personal-details-first-name'"
                                [group]="formGroup"
                            >
                            </lsq-input>
                        </div>
                        <div class="col-sm-6">
                            <lsq-input
                                class="e2e-last-name"
                                formControlName="lastName"
                                i18n-label="New user last name input"
                                label="Last Name"
                                [name]="'personal-details-last-name'"
                                [group]="formGroup"
                            >
                            </lsq-input>
                        </div>
                        <div class="col-12">
                            <lsq-input
                                class="e2e-job-title"
                                formControlName="title"
                                i18n-label="New user title input"
                                label="Title"
                                [name]="'personal-details-title'"
                                [group]="formGroup"
                            >
                            </lsq-input>
                        </div>
                        <div class="col-md-8">
                            <lsq-phone-input
                                class="e2e-phone-number"
                                formControlName="phoneNumber"
                                i18n-label="New user phone number input"
                                [hasInnerLabel]="true"
                                label="Phone Number"
                                [name]="'personal-details-phone-number'"
                                [group]="formGroup"
                            >
                            </lsq-phone-input>
                        </div>
                        <div class="col-md-4">
                            <lsq-input
                                formControlName="phoneNumberExtension"
                                i18n-label="New user extension input"
                                label="Ext"
                                [name]="'personal-details-extension'"
                                type="tel"
                                [group]="formGroup"
                            >
                            </lsq-input>
                        </div>
                        <div class="col-12">
                            <lsq-input
                                class="e2e-email-address"
                                formControlName="emailAddress"
                                i18n-label="New user email address input"
                                label="Email Address"
                                type="email"
                                [name]="'personal-details-email-address'"
                                [group]="formGroup"
                            >
                            </lsq-input>
                        </div>
                    </div>
                </fieldset>
            </div>
            <div class="modal-footer ok-btn-showing">
                <div class="modal-footer-left">
                    <lsq-button appearance="transparent" [tabindex]="'-1'">
                        <a
                            href="https://www.lsq.com/privacy"
                            target="_blank"
                            i18n="Privacy policy link"
                        >
                            Privacy Policy
                        </a>
                    </lsq-button>
                </div>
                <div class="modal-footer-right">
                    <lsq-button
                        size="sm"
                        type="submit"
                        [disabled]="!formGroup.valid"
                        i18n="Continue button"
                    >
                        Continue
                    </lsq-button>
                </div>
            </div>
        </form>
    `,
    styleUrls: ['./add-user-personal-details-modal.component.scss']
})
export class AddUserPersonalDetailsModalComponent implements OnInit {
    public endpointProperties = [
        {
            name: 'companyKey',
            type: 'Guid',
            defaultValue: null,
            validators: [Validators.required, Validators.pattern(Regex.Guid)]
        },
        {
            name: 'firstName',
            type: 'string',
            defaultValue: null,
            validators: [Validators.required, this._stringI18nValidator.maxLength(127)]
        },
        {
            name: 'middleName',
            type: 'string',
            defaultValue: null,
            validators: [this._stringI18nValidator.maxLength(127)]
        },
        {
            name: 'lastName',
            type: 'string',
            defaultValue: null,
            validators: [Validators.required, this._stringI18nValidator.maxLength(127)]
        },
        {
            name: 'title',
            type: 'string',
            defaultValue: null,
            validators: [Validators.required, this._stringI18nValidator.maxLength(255)]
        },
        {
            name: 'salutation',
            type: 'select',
            nullable: true,
            options: Salutation,
            defaultValue: null,
            validators: []
        },
        {
            name: 'emailAddress',
            type: 'string',
            defaultValue: null,
            validators: [Validators.required, this._emailI18nValidator.email()]
        },
        {
            name: 'phoneNumber',
            type: 'string',
            defaultValue: null,
            validators: [Validators.required, this._phoneI18nValidator.phone()]
        },
        {
            name: 'phoneNumberExtension',
            type: 'string',
            defaultValue: null,
            validators: [this._stringI18nValidator.maxLength(255)]
        }
    ];
    public subtitle = this._i18n({
        value:
            'The person you invite will receive an email with an invitation link that will expire in 12 hours. Once they click the link they can choose their password and register.'
    });
    public modal: Modal;
    public formGroup: FormGroup;
    public icons = Icons;
    public color = Color;
    public masks = Masks;
    public unmasks = Unmasks;

    constructor(
        public modalService: ModalService,
        private _manageUsersService: ManageUsersService,
        private _modalWorkflowService: ModalWorkflowService,
        private _stringI18nValidator: StringI18nValidator,
        private _phoneI18nValidator: PhoneI18nValidator,
        private _emailI18nValidator: EmailI18nValidator,
        private _i18n: I18n
    ) {}

    public ngOnInit(): void {
        this.modal = this.modalService.modal;
        const formGroupConfig = this.endpointProperties
            .filter((property) => ['companyKey', 'claims'].indexOf(property.name) === -1)
            .reduce((_formGroupConfig, property) => {
                _formGroupConfig[property.name] = new FormControl(
                    property.defaultValue,
                    property.validators
                );
                return _formGroupConfig;
            }, {});
        this.formGroup = new FormGroup(formGroupConfig);
        if (!!this._manageUsersService.addUserState.formValue) {
            this.formGroup.patchValue(this._manageUsersService.addUserState.formValue);
        }

        this.formGroup.valueChanges.subscribe((value) => {
            this._manageUsersService.updateAddUserFormValue(value);
        });
    }

    public continueWorkflow(): void {
        if (this.formGroup.valid) {
            this._modalWorkflowService.nextStep();
        }
    }
}
